import React from 'react'
import { ArrowLeft } from 'react-feather'
import { match } from 'ts-pattern'

import { InformationCard } from '@frame/ui-components/information-card'
import { SlideUpPopover } from '@frame/ui-components/slide-up-popover'
import { UnorderedList } from '@frame/ui-components/unordered-list'
import { Button } from '@frame/ui-primitives/button'
import { H3 } from '@frame/ui-primitives/h3'

import { useDocumentsFlowContext } from '@documents/contexts/documents-flow-context'
import { DocumentTypeOption } from '@documents/types'
import { OfferLetterInfo } from '@documents/v2/screens/upload-documents/more-info/offer-letter-info'
import { getW2Year } from '@documents/v2/utils/getW2Year'
import { DocumentIncomeRequirementFileType } from '@frame/lib/api/tasks/types'

import { SUPPORTED_DOCUMENT_TYPES } from '../../../../constants'
import { PaystubInfo } from './paystub-info'
import { W2Info } from './w2-info'

// TODO replace this with a real ISO 8601 duration parser
function getDaysFromIsoDuration(duration: string): string {
  const dayMatch = duration.match(/^P(\d+)D$/)

  if (!dayMatch) {
    throw new Error(`Invalid ISO 8601 duration format`)
  }

  const days = parseInt(dayMatch[1], 10)
  return String(days)
}

function MoreInfoCard({ onClick }: { onClick?: () => void }) {
  const { selectedDocumentType, documentIncomeRequirements, hideW2s } = useDocumentsFlowContext()

  const selectedSupportedDocumentType = selectedDocumentType && SUPPORTED_DOCUMENT_TYPES.includes(selectedDocumentType)

  if (!selectedSupportedDocumentType) return null

  const { w2Year } = getW2Year()

  return match(selectedDocumentType)
    .with(DocumentTypeOption.PAYSTUB_OR_W2, () => (
      <InformationCard onClick={onClick}>
        <UnorderedList
          items={(() => {
            const items = []
            if (documentIncomeRequirements) {
              const requirements = documentIncomeRequirements.requirements || []
              const paystubRequirement = requirements.find(
                (req) => req.file_type === DocumentIncomeRequirementFileType.PAYSTUB
              )
              if (paystubRequirement) {
                items.push(
                  'Paystubs must cover last ' +
                    (paystubRequirement.lookback_period
                      ? getDaysFromIsoDuration(paystubRequirement.lookback_period)
                      : '30') +
                    ' days'
                )
              }
            } else {
              items.push('Paystubs must cover last 30 days')
            }
            if (!hideW2s) {
              items.push(`W2s must cover ${w2Year}`)
            }
            return items
          })()}
        />
      </InformationCard>
    ))
    .with(DocumentTypeOption.OFFER_LETTER, () => (
      <InformationCard onClick={onClick}>
        <UnorderedList
          items={[
            'Must be the original offer letter PDF from your employer',
            'Your start date must be no more than 30 days in the past',
          ]}
        />
      </InformationCard>
    ))
    .otherwise(() => null)
}

function MoreInfoPopover({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { selectedDocumentType, hideW2s } = useDocumentsFlowContext()

  return match(selectedDocumentType)
    .with(DocumentTypeOption.PAYSTUB_OR_W2, () => (
      <SlideUpPopover show={isOpen} hide={onClose}>
        <div className="mb-4 flex items-center space-x-2">
          <Button aria-label="Back" onClick={onClose} Icon={ArrowLeft} border={false} intent="secondary" />
          <H3>Frequently asked questions</H3>
        </div>
        <div className="flex flex-col gap-3">
          <PaystubInfo />
          {!hideW2s && <W2Info />}
        </div>
      </SlideUpPopover>
    ))
    .with(DocumentTypeOption.OFFER_LETTER, () => (
      <SlideUpPopover show={isOpen} hide={onClose}>
        <div className="mb-4 flex items-center space-x-2">
          <Button aria-label="Back" onClick={onClose} Icon={ArrowLeft} border={false} intent="secondary" />
          <H3>Frequently asked questions</H3>
        </div>
        <div className="flex flex-col gap-3">
          <OfferLetterInfo />
        </div>
      </SlideUpPopover>
    ))
    .otherwise(() => null)
}

export { MoreInfoCard, MoreInfoPopover }
